import { useState, useEffect } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import { useAuthStore } from "@/stores/authStore"
import AppLayout from "@/components/shared/AppLayout"
import AuthLayout from "@/components/shared/AuthLayout"
import PublicLayout from "@/components/shared/PublicLayout"
import { MARKETPLACE_ENABLED, BILLING_ENABLED } from "@/lib/appConfig"

import { Capacitor } from "@capacitor/core"
import { StatusBar, Style } from "@capacitor/status-bar"
import { App as CapApp } from "@capacitor/app"
import { Network } from "@capacitor/network"
import { toast } from "sonner"

// Public Market pages
import LandingPage from "@/app/landing/LandingPage"
import PublicListingsPage from "@/app/public/PublicListingsPage"
import PublicListingDetailPage from "@/app/public/PublicListingDetailPage"
import PublicLandlordProfilePage from "@/app/public/PublicLandlordProfilePage"

// Auth pages
import LoginPage from "@/app/auth/LoginPage"
import RegisterPage from "@/app/auth/RegisterPage"
import ForgotPasswordPage from "@/app/auth/ForgotPasswordPage"
import ResetPasswordPage from "@/app/auth/ResetPasswordPage"
import VerifyEmailPage from "@/app/auth/VerifyEmailPage"

// App pages
import DashboardPage from "@/app/dashboard/DashboardPage"
import PropertiesPage from "@/app/properties/PropertiesPage"
import PropertyDetailPage from "@/app/properties/PropertyDetailPage"
import TenantsPage from "@/app/tenants/TenantsPage"
import TenantDetailPage from "@/app/tenants/TenantDetailPage"
import LeasesPage from "@/app/leases/LeasesPage"
import LeaseDetailPage from "@/app/leases/LeaseDetailPage"
import FinancesPage from "@/app/finances/FinancesPage"
import MaintenancePage from "@/app/maintenance/MaintenancePage"
import MaintenanceDetailPage from "@/app/maintenance/MaintenanceDetailPage"
import DocumentsPage from "@/app/documents/DocumentsPage"
import MarketplacePage from "@/app/marketplace/MarketplacePage"
import NewMarketplaceListingPage from "@/app/marketplace/NewMarketplaceListingPage"
import EditMarketplaceListingPage from "@/app/marketplace/EditMarketplaceListingPage"
import SettingsPage from "@/app/settings/SettingsPage"
import EditProfilePage from "@/app/settings/EditProfilePage"
import ChangePasswordPage from "@/app/settings/ChangePasswordPage"
import PricingPage from "@/app/billing/PricingPage"
import BillingSuccessPage from "@/app/billing/BillingSuccessPage"
import MorePage from "@/app/MorePage"
import NotFoundPage from "@/app/NotFoundPage"
import AdminLayout from "@/app/admin/AdminLayout"
import AdminDashboardPage from "@/app/admin/AdminDashboardPage"
import AdminUsersPage from "@/app/admin/AdminUsersPage"
import AdminPropertiesPage from "@/app/admin/AdminPropertiesPage"
import AdminPlansPage from "@/app/admin/AdminPlansPage"
import AdminCouponsPage from "@/app/admin/AdminCouponsPage"
import AdminLoginPage from "@/app/admin/AdminLoginPage"
import AdminFeatureRequestsPage from "@/app/admin/AdminFeatureRequestsPage"

// Blog and Public Pages
import BlogListPage from "@/app/blog/BlogListPage"
import BlogPostPage from "@/app/blog/BlogPostPage"
import PublicRoadmapPage from "@/app/public/PublicRoadmapPage"

// Tools Hub
import ToolsHubPage from "@/app/tools/ToolsHubPage"
import ToolPageLayout from "@/app/tools/ToolPageLayout"
import ToolDynamicRouter from "@/app/tools/ToolDynamicRouter"

// Legal Pages
import PrivacyPolicyPage from "@/app/legal/PrivacyPolicyPage"
import TermsOfServicePage from "@/app/legal/TermsOfServicePage"

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireGuest({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function RequireAdminAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />
  if (user?.role !== "superadmin") return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function RequireAdminGuest({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (isAuthenticated && user?.role === "superadmin") {
    return <Navigate to="/admin" replace />
  }
  return <>{children}</>
}

function RootRoute() {
  // Logged-in users can still view the landing page (no redirect)
  if (Capacitor.isNativePlatform()) return <Navigate to="/login" replace />
  return <LandingPage />
}

export default function App() {
  const [hydrated, setHydrated] = useState(
    () => useAuthStore.persist.hasHydrated()
  )

  // Native platform setup (runs once on mount)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    // Status bar: dark icons on white background
    StatusBar.setStyle({ style: Style.Dark }).catch(() => {})
    StatusBar.setBackgroundColor({ color: "#ffffff" }).catch(() => {})

    // Android hardware back button
    const backHandler = CapApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back()
      } else {
        CapApp.exitApp()
      }
    })

    // Network status banner
    const networkHandler = Network.addListener("networkStatusChange", (status) => {
      if (!status.connected) {
        toast.warning("No internet connection")
      }
    })

    return () => {
      backHandler.then((h) => h.remove())
      networkHandler.then((h) => h.remove())
    }
  }, [])

  useEffect(() => {
    if (!hydrated) {
      const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true))
      return unsub
    }
  }, [hydrated])

  if (!hydrated) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <Routes>
      {/* Public Routes without layout */}
      <Route path="/" element={<RootRoute />} />

      {/* Auth routes */}
      <Route
        element={
          <RequireGuest>
            <AuthLayout />
          </RequireGuest>
        }
      >
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      {/* Public Market routes — only when marketplace is enabled */}
      {MARKETPLACE_ENABLED && (
        <Route element={<PublicLayout />}>
          <Route path="/listings" element={<PublicListingsPage />} />
          <Route path="/listings/:slug" element={<PublicListingDetailPage />} />
          <Route path="/landlord/:slug" element={<PublicLandlordProfilePage />} />
        </Route>
      )}

      {/* Blog & Public Content Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/roadmap" element={<PublicRoadmapPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
      </Route>

      {/* Tools Routes */}
      <Route path="/tools" element={<ToolsHubPage />} />
      <Route element={<ToolPageLayout />}>
        <Route path="/tools/:slug" element={<ToolDynamicRouter />} />
      </Route>

      {/* Legal Routes */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />

      {/* App routes */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/properties/:id" element={<PropertyDetailPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/tenants/:id" element={<TenantDetailPage />} />
        <Route path="/leases" element={<LeasesPage />} />
        <Route path="/leases/:id" element={<LeaseDetailPage />} />
        <Route path="/finances" element={<FinancesPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/maintenance/:id" element={<MaintenanceDetailPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        {MARKETPLACE_ENABLED && (
          <>
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/listings/new" element={<NewMarketplaceListingPage />} />
            <Route path="/listings/:id/edit" element={<EditMarketplaceListingPage />} />
          </>
        )}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/profile" element={<EditProfilePage />} />
        <Route path="/settings/change-password" element={<ChangePasswordPage />} />
        {BILLING_ENABLED && <Route path="/pricing" element={<PricingPage />} />}
        <Route path="/more" element={<MorePage />} />
      </Route>

      {/* Admin routes */}
      <Route
        element={
          <RequireAdminGuest>
            <AuthLayout />
          </RequireAdminGuest>
        }
      >
        <Route path="/admin/login" element={<AdminLoginPage />} />
      </Route>

      <Route
        element={
          <RequireAdminAuth>
            <AdminLayout />
          </RequireAdminAuth>
        }
      >
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/properties" element={<AdminPropertiesPage />} />
        <Route path="/admin/plans" element={<AdminPlansPage />} />
        <Route path="/admin/coupons" element={<AdminCouponsPage />} />
        <Route path="/admin/feature-requests" element={<AdminFeatureRequestsPage />} />
      </Route>

      {/* Billing success — outside app layout (full page) */}
      {BILLING_ENABLED && <Route path="/billing/success" element={<BillingSuccessPage />} />}

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
